import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faUser, faSpinner, faCheck, faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import { QRCodeSVG } from 'qrcode.react';
import BankIdLogo from '../../bankid/BankID_logo_black.svg';
import BankIdLogoWhite from '../../bankid/BankID_logo_white.svg';
import './AccountVerification.css';
import { trackFormSubmission } from '../../utils/analytics';

function AccountVerification({ onLogin }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [bankIdLoading, setBankIdLoading] = useState(false);
  const [orderRef, setOrderRef] = useState(null);
  const [qrCodeData, setQrCodeData] = useState('');
  const [qrExpiry, setQrExpiry] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [bankIdMethod, setBankIdMethod] = useState('qrcode');
  const [useQrFallback, setUseQrFallback] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  
  const pollIntervalRef = useRef(null);
  const qrRefreshTimerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const backend_url = config.backend_url;
  
  // Get token from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');

  // Enhanced mobile/tablet detection
  useEffect(() => {
    const checkMobile = () => {
      const forceMobile = new URLSearchParams(window.location.search).get('mobile') === 'true';
      
      if (forceMobile) {
        setIsMobile(true);
        setBankIdMethod('mobile');
        return;
      }
      
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Enhanced regex to detect both mobile phones and tablets
      const mobileTabletRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|Tab|Kindle|Silk|PlayBook|Mobile|Touch/i;
      
      // iPad detection - comprehensive check for all iPad variants including iPad Pro
      const isIPad = /iPad/i.test(userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !window.MSStream) ||
                    /iPad|iPad Pro|iPad Air|iPad Mini/i.test(userAgent) ||
                    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      
      // Windows tablet detection (Surface Pro, Surface Book, etc.)
      const isWindowsTablet = /Windows NT.*Touch/i.test(userAgent) ||
                             /Surface/i.test(userAgent) ||
                             (navigator.platform === 'Win32' && navigator.maxTouchPoints > 1) ||
                             /Tablet PC/i.test(userAgent);
      
      // Android tablet detection - improved to catch more variants
      const isAndroidTablet = /Android/i.test(userAgent) && !/Mobile/i.test(userAgent) ||
                             /Android.*Tablet/i.test(userAgent) ||
                             /SM-T|Galaxy Tab|Nexus [0-9]|Pixel C|Pixel Slate/i.test(userAgent);
      
      // Additional tablet detection for other brands
      const isOtherTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i.test(userAgent.toLowerCase()) ||
                           /Kindle|Fire|GT-P|GT-N|SM-P|MediaPad|Transformer|TF[0-9]|Xoom|MZ[0-9]/i.test(userAgent);
      
      // Touch capability check for modern devices
      const hasTouchCapability = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
      
      // Screen size based detection for edge cases (tablets typically have larger screens)
      const screenBasedTabletCheck = hasTouchCapability && 
                                    window.screen.width >= 768 && 
                                    window.screen.height >= 768;
      
      const isMobileOrTablet = mobileTabletRegex.test(userAgent) || 
                              isIPad || 
                              isWindowsTablet || 
                              isAndroidTablet || 
                              isOtherTablet ||
                              screenBasedTabletCheck;
      
      setIsMobile(isMobileOrTablet);
      
      // If mobile or tablet device, automatically set bankIdMethod to 'mobile'
      if (isMobileOrTablet) {
        setBankIdMethod('mobile');
      }
    };
    
    checkMobile();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearPollingAndTimers();
    };
  }, []);

  // Effect to refresh QR code when method is qrcode
  useEffect(() => {
    if (bankIdMethod === 'qrcode' || (isMobile && useQrFallback)) {
      // Initialize QR code on first selection or when mobile user chooses QR fallback
      if (tokenValid && !bankIdLoading) {
        generateQrCode();
      }
    } else if (bankIdMethod === 'mobile' && isMobile && !useQrFallback) {
      // Always show the button first instead of auto-starting BankID
      setBankIdLoading(false);
      
      // Clear any stored BankID session to ensure a fresh start
      localStorage.removeItem('bankid_orderRef_verification');
      localStorage.removeItem('bankid_timestamp_verification');
      localStorage.removeItem('bankid_launch_time_verification');
      localStorage.removeItem('bankid_url_verification');
    } else {
      // Clean up QR code related timers when not using QR code method
      if (qrRefreshTimerRef.current) {
        clearTimeout(qrRefreshTimerRef.current);
        qrRefreshTimerRef.current = null;
      }
      setQrCodeData('');
    }
  }, [bankIdMethod, useQrFallback, isMobile, tokenValid, bankIdLoading]);

  // Effect to check for returning from BankID app
  useEffect(() => {
    const checkPendingBankIdSession = () => {
      // Don't check if we're already polling to avoid duplicate sessions
      if (pollIntervalRef.current) {
        return;
      }

      // On mobile devices, don't auto-resume sessions when the verification page opens
      if (isMobile) {
        return;
      }

      const storedOrderRef = localStorage.getItem('bankid_orderRef_verification');
      const timestamp = localStorage.getItem('bankid_timestamp_verification');
      
      // Only check if we have a stored orderRef and it's less than 10 minutes old
      if (storedOrderRef && timestamp) {
        const sessionAge = Date.now() - parseInt(timestamp);
        const sessionValid = sessionAge < 10 * 60 * 1000; // 10 minutes
        
        if (sessionValid) {
          // Resume BankID session
          setOrderRef(storedOrderRef);
          setBankIdLoading(true);
          startPolling(storedOrderRef);
        } else {
          // Clear expired session
          localStorage.removeItem('bankid_orderRef_verification');
          localStorage.removeItem('bankid_timestamp_verification');
          localStorage.removeItem('bankid_launch_time_verification');
          localStorage.removeItem('bankid_url_verification');
        }
      }
    };
    
    // Check for pending session when component mounts, but with a slight delay
    const initTimer = setTimeout(() => {
      checkPendingBankIdSession();
    }, 500);
    
    // Also check when the window regains focus (user returns from BankID app)
    const handleFocus = () => {
      localStorage.setItem('bankid_last_focus_verification', Date.now().toString());
      
      setTimeout(() => {
        checkPendingBankIdSession();
      }, 300);
    };
    
    // Check when visibility changes (more reliable than focus on some browsers)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        localStorage.setItem('bankid_last_focus_verification', Date.now().toString());
        
        setTimeout(() => {
          checkPendingBankIdSession();
        }, 300);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(initTimer);
    };
  }, [isMobile]);

  // Add effect to clear any leftover BankID sessions on component mount
  useEffect(() => {
    const cleanupExistingSessions = () => {
      const storedOrderRef = localStorage.getItem('bankid_orderRef_verification');
      
      if (storedOrderRef) {
        // Clear all BankID related data to ensure a fresh start
        localStorage.removeItem('bankid_orderRef_verification');
        localStorage.removeItem('bankid_timestamp_verification');
        localStorage.removeItem('bankid_launch_time_verification');
        localStorage.removeItem('bankid_url_verification');
        localStorage.removeItem('bankid_last_focus_verification');
      }
    };
    
    cleanupExistingSessions();
  }, []);

  // Verify token on component mount
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setError('Ingen verifieringstoken hittades.');
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${backend_url}/api/verify-token`, {
        token: token
      });

      if (response.data.success) {
        setTokenValid(true);
        setSellerInfo(response.data.seller);
      } else {
        setError('Ogiltig eller utgången verifieringstoken.');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      if (error.response?.status === 404) {
        setError('Verifieringstoken hittades inte.');
      } else if (error.response?.status === 400) {
        setError('Verifieringstoken har gått ut. Begär en ny länk.');
      } else {
        setError('Ett fel uppstod vid verifiering av token.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQrCode = async () => {
    try {
      setBankIdLoading(true);
      setError('');
      
      // Request a QR code auth session from backend
      const authResponse = await axios.post(`${backend_url}/bankid/auth`, {
        qr_code: true
      });

      const { orderRef, qrCode, autoStartToken } = authResponse.data;
      
      if (!orderRef) {
        console.error("Missing orderRef in response");
        setError('Kunde inte generera QR-kod: Saknar orderRef.');
        setBankIdLoading(false);
        return;
      }
      
      // Set order reference for polling
      setOrderRef(orderRef);
      
      // For BankID QR codes, try different formats depending on what the backend provided
      let qrData;
      
      if (autoStartToken) {
        // Format for BankID app with autoStartToken
        qrData = `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
      } else if (qrCode) {
        // Use provided QR code data if available
        qrData = qrCode;
      } else {
        // Use standard BankID format as fallback
        qrData = `bankid://${orderRef}`;
      }
      
      setQrCodeData(qrData);
      
      // Set expiry time for QR - usually 30-60 seconds before refresh
      const expiryTime = new Date();
      expiryTime.setSeconds(expiryTime.getSeconds() + 30); // 30 seconds expiry
      setQrExpiry(expiryTime);
      
      // Start polling for auth status
      startPolling(orderRef);
      
      // Schedule next QR refresh
      scheduleQrRefresh();
      
    } catch (error) {
      console.error("Error generating QR code:", error);
      console.error("Error details:", error.response?.data);
      setError('Kunde inte generera QR-kod. Försök igen senare.');
      setBankIdLoading(false);
    }
  };

  const scheduleQrRefresh = () => {
    // Clear any existing timer
    if (qrRefreshTimerRef.current) {
      clearTimeout(qrRefreshTimerRef.current);
    }
    
    // Schedule refresh for 30 seconds later
    qrRefreshTimerRef.current = setTimeout(() => {
      if (bankIdMethod === 'qrcode' || (isMobile && useQrFallback)) {
        generateQrCode();
      }
    }, 30000); // 30 seconds
  };

  // Function to launch BankID on mobile devices
  const launchMobileBankId = async () => {
    // Don't start a new session if we're already loading
    if (bankIdLoading) {
      return;
    }
    
    setBankIdLoading(true);
    setError('');

    try {
      // First, clean up any existing sessions to avoid conflicts
      localStorage.removeItem('bankid_orderRef_verification');
      localStorage.removeItem('bankid_timestamp_verification');
      localStorage.removeItem('bankid_launch_time_verification');
      localStorage.removeItem('bankid_url_verification');
      clearPollingAndTimers();
      
      // Request auth session from backend
      const authResponse = await axios.post(`${backend_url}/bankid/auth`, {
        qr_code: true // Request autoStartToken for mobile launch
      });

      const { orderRef, autoStartToken } = authResponse.data;
      
      if (!orderRef) {
        console.error("Missing orderRef in response");
        setError('Kunde inte starta BankID: Saknar orderRef.');
        setBankIdLoading(false);
        return;
      }
      
      // Store the order reference for resuming session later
      localStorage.setItem('bankid_orderRef_verification', orderRef);
      localStorage.setItem('bankid_timestamp_verification', Date.now().toString());
      
      // Start polling BEFORE launching the app
      startPolling(orderRef);
      
      // Wait a moment to ensure polling is properly established
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Determine best URL format based on available data
      let bankIdUrl;
      let urlType;
      
      if (autoStartToken) {
        // Preferred format with autoStartToken (most reliable)
        bankIdUrl = `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
        urlType = "autoStartToken";
      } else {
        // Fallback to simpler format with just orderRef
        bankIdUrl = `bankid://${orderRef}`;
        urlType = "orderRef";
      }
      
      localStorage.setItem('bankid_url_verification', bankIdUrl);
      
      // Create an invisible iframe for initial launch attempt
      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = bankIdUrl;
        document.body.appendChild(iframe);
        
        // Remove iframe after a moment
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 500);
      } catch (iframeError) {
        // Iframe launch failed, using direct method
      }
      
      // Direct launch as backup - most universally supported method
      setTimeout(() => {
        window.location.href = bankIdUrl;
      }, 100);
      
    } catch (error) {
      console.error("Error launching mobile BankID:", error);
      console.error("Error response:", error.response?.data || error.message);
      setError('Kunde inte starta BankID-appen. Försök igen senare.');
      setBankIdLoading(false);
    }
  };

  const startPolling = (orderRef) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    // Set the order reference state
    setOrderRef(orderRef);
    
    // Store in localStorage for session resumption
    localStorage.setItem('bankid_orderRef_verification', orderRef);
    localStorage.setItem('bankid_timestamp_verification', Date.now().toString());
    
    setPollCount(0);
    pollIntervalRef.current = setInterval(() => {
      setPollCount(prevCount => prevCount + 1);
      pollBankIdStatus(orderRef);
    }, 2000);
    
    // Set a maximum polling time (5 minutes)
    setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setError('BankID-processen tog för lång tid. Vänligen försök igen.');
        setBankIdLoading(false);
        localStorage.removeItem('bankid_orderRef_verification');
        localStorage.removeItem('bankid_timestamp_verification');
        localStorage.removeItem('bankid_launch_time_verification');
        localStorage.removeItem('bankid_url_verification');
      }
    }, 300000); // 5 minutes
  };

  const pollBankIdStatus = async (orderRef) => {
    try {
      // Check if we still have a valid session
      const storedOrderRef = localStorage.getItem('bankid_orderRef_verification');
      if (storedOrderRef !== orderRef) {
        return; // Don't continue polling if the orderRef has changed
      }
      
      const statusResponse = await axios.post(`${backend_url}/bankid/collect`, {
        order_ref: orderRef
      });

      const { status, hintCode, completionData } = statusResponse.data;

      if (status === 'complete') {
        // Stop polling immediately
        clearPollingAndTimers();
        
        // Clean up stored BankID session data
        localStorage.removeItem('bankid_orderRef_verification');
        localStorage.removeItem('bankid_timestamp_verification');
        localStorage.removeItem('bankid_launch_time_verification');
        localStorage.removeItem('bankid_url_verification');
        
        // Update UI state
        setBankIdLoading(false);
        
        // Extract user data from BankID response
        const userData = completionData?.user || {};
        let personnummer = userData.personnummer || userData.personalNumber;
        let firstname = userData.givenName || userData.firstname;
        let lastname = userData.surname || userData.lastname;
        
        // Try to extract from different possible locations in the response
        if (!personnummer && completionData) {
          // Try direct access to completionData fields
          personnummer = completionData.personnummer || completionData.personalNumber;
          firstname = firstname || completionData.givenName || completionData.firstname;
          lastname = lastname || completionData.surname || completionData.lastname;
        }
        
        // Also check for data stored in local storage from BankID session
        if (!personnummer) {
          const storedPersonnummer = localStorage.getItem('personnummer');
          if (storedPersonnummer) {
            personnummer = storedPersonnummer;
          }
        }
        
        if (!personnummer) {
          setError('Kunde inte hämta personnummer från BankID. Försök igen.');
          console.error('BankID completion data:', completionData);
          return;
        }
        
        // Upgrade account with BankID information
        await upgradeAccount(personnummer, firstname, lastname);
        
      } else if (status === 'failed') {
        console.error("BankID authentication failed:", hintCode);
        
        // Clean up
        clearPollingAndTimers();
        localStorage.removeItem('bankid_orderRef_verification');
        localStorage.removeItem('bankid_timestamp_verification');
        localStorage.removeItem('bankid_launch_time_verification');
        localStorage.removeItem('bankid_url_verification');
        setBankIdLoading(false);
        
        // Display error based on hint code
        const errorMessages = {
          'cancelled': 'BankID-verifieringen avbröts.',
          'userCancel': 'Du avbröt BankID-verifieringen.',
          'expiredTransaction': 'BankID-verifieringen tog för lång tid. Försök igen.',
          'certificateErr': 'Problem med BankID-certifikatet. Prova att uppdatera din BankID-app.',
          'userSign': 'Fel vid BankID-signering.',
          'startFailed': 'BankID-verifieringen kunde inte startas.',
          'outstandingTransaction': 'Du har redan en pågående BankID-session. Avsluta den och försök igen.'
        };
        
        setError(errorMessages[hintCode] || `BankID-verifiering misslyckades: ${hintCode}`);
      } else if (status === 'pending') {
        // Only log hintCode for pending status
        if (hintCode) {
          // If we get outstandingTransaction, we may need to restart
          if (hintCode === 'outstandingTransaction') {
            // No action needed - just let the user know in the UI what's happening
          }
        }
      }
    } catch (error) {
      console.error("Error polling BankID status:", error);
      
      // Handle critical errors only - temp network errors should continue polling
      if (error.response?.status === 400 || error.response?.status === 404) {
        console.error("Critical error - stopping polling");
        clearPollingAndTimers();
        localStorage.removeItem('bankid_orderRef_verification');
        localStorage.removeItem('bankid_timestamp_verification');
        localStorage.removeItem('bankid_launch_time_verification');
        localStorage.removeItem('bankid_url_verification');
        setBankIdLoading(false);
        setError('Ett fel uppstod vid kontroll av BankID-status. Försök igen senare.');
      }
    }
  };

  const clearPollingAndTimers = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (qrRefreshTimerRef.current) {
      clearTimeout(qrRefreshTimerRef.current);
      qrRefreshTimerRef.current = null;
    }
  };

  // Calculate time remaining for QR code
  const getQrTimeRemaining = () => {
    if (!qrExpiry) return null;
    
    const now = new Date();
    const diffMs = qrExpiry - now;
    
    if (diffMs <= 0) return 0;
    
    return Math.ceil(diffMs / 1000); // Return seconds remaining
  };

  const timeRemaining = getQrTimeRemaining();

  // Function to handle BankID refresh when there are stuck sessions
  const handleRefresh = () => {
    // Clear everything first
    clearPollingAndTimers();
    localStorage.removeItem('bankid_orderRef_verification');
    localStorage.removeItem('bankid_timestamp_verification');
    localStorage.removeItem('bankid_launch_time_verification');
    localStorage.removeItem('bankid_url_verification');
    
    // Reset UI state to show the "Starta BankID" button
    setBankIdLoading(false);
    setError('');
  };

  const upgradeAccount = async (personnummer, firstname, lastname) => {
    try {
      const response = await axios.post(`${backend_url}/api/upgrade-account`, {
        token: token,
        personnummer: personnummer,
        firstname: firstname,
        lastname: lastname
      }, { withCredentials: true });

      if (response.data.success) {
        setBankIdLoading(false);
        setVerificationSuccess(true);
        
        const seller = response.data.seller;
        
        // Use the onLogin prop to update the global app state
        if (onLogin) {
          onLogin('seller', seller.id, seller.firstname, seller.lastname);
        }

        // Store any additional info not handled by onLogin
        localStorage.setItem('personnummer', personnummer);
        if (seller.email) localStorage.setItem('email', seller.email);
        
        // Set a timestamp to help debug state issues
        localStorage.setItem('loginTimestamp', Date.now().toString());
        
        // Set up axios authentication for subsequent requests
        if (axios.defaults.headers.common) {
          // The session cookie should be set by the server response
          delete axios.defaults.headers.common['Authorization']; // Clear any old tokens
        }
        
        // Log the current localStorage state for debugging
        console.log('AccountVerification: Set localStorage values:', {
          loggedIn: localStorage.getItem('loggedIn'),
          userType: localStorage.getItem('userType'),
          userId: localStorage.getItem('userId'),
          firstname: localStorage.getItem('firstname'),
          lastname: localStorage.getItem('lastname')
        });
        
        // Track successful verification
        trackFormSubmission('account_verification_success', {
          login_method: 'bankid',
          user_type: 'seller',
          device_type: isMobile ? 'mobile' : 'desktop',
          context: 'account_verification'
        });
        
        // Redirect using history.push instead of a hard refresh
        setTimeout(() => {
          console.log('AccountVerification: Redirecting to home page...');
          history.push('/');
        }, 800);
        
      } else {
        throw new Error(response.data.error || 'Account upgrade failed');
      }
    } catch (error) {
      console.error('Error upgrading account:', error);
      setBankIdLoading(false);
      setError(error.response?.data?.error || 'Ett fel uppstod vid kontouppgradering. Försök igen.');
    }
  };

  const handleGoHome = () => {
    history.push('/');
  };

  if (loading) {
    return (
      <div className="verification-container">
        <div className="verification-loading">
          <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
          <p>Verifierar token...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verification-container">
        <div className="verification-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h2>Verifiering misslyckades</h2>
          <p>{error}</p>
          <button onClick={handleGoHome} className="home-button">
            Tillbaka till startsidan
          </button>
        </div>
      </div>
    );
  }

  if (verificationSuccess) {
    return (
      <div className="verification-container">
        <div className="verification-success">
          <FontAwesomeIcon icon={faCheck} className="success-icon" />
          <h2>Konto verifierat!</h2>
          <p>Ditt konto har uppgraderats och kopplats till din e-postadress.</p>
          <p>Du är nu inloggad och omdirigeras till startsidan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <div className="verification-content">
        <div className="verification-header">
          <FontAwesomeIcon icon={faUser} className="shield-icon" />
          <p>Uppgradera ditt konto med BankID för säker åtkomst</p>
        </div>

        <div className="bankid-logo-container">
          <img src={BankIdLogo} alt="BankID" className="bankid-logo" />
        </div>

        {sellerInfo && (
          <div className="account-info">
            <h3>Kontoinformation</h3>
            <p><strong>E-post:</strong> {sellerInfo.email}</p>
            <p><strong>Status:</strong> {sellerInfo.is_verified ? 'Verifierat' : 'Ej verifierat'}</p>
            {sellerInfo.has_names && (
              <p><strong>Namn:</strong> Redan registrerat</p>
            )}
          </div>
        )}

        <div className="verification-instructions">
          <h3>Varför verifiera?</h3>
          <ul>
            <li>Säker inloggning med BankID</li>
            <li>Hantera och redigera dina annonser</li>
            <li>Kommunicera direkt med mäklare</li>
            <li>Få notifieringar om intresse</li>
          </ul>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="bankid-section">
          <p className="bankid-disclaimer">
            Verifiera med BankID – Bodex säkerställer din identitet och mäklarnas legitimitet för en trygg och tillförlitlig plattform.
          </p>
          
          {/* On mobile, show direct BankID launch button */}
          {isMobile ? (
            <div className="mobile-bankid-container">
              {!useQrFallback ? (
                // Mobile BankID flow
                <>
                  {bankIdLoading ? (
                    <div className="loading-status">
                      <div className="loading-indicator"></div>
                      <p>Startar BankID...</p>
                      {pollCount > 0 && (
                        <p className="poll-count">Väntat: {pollCount} sekunder</p>
                      )}
                      <p className="loading-hint">Om BankID-appen inte startar inom några sekunder, klicka på knappen nedan.</p>
                      <span 
                        onClick={() => {
                          handleRefresh();
                          setUseQrFallback(true);
                        }}
                        style={{ 
                          textDecoration: 'underline', 
                          cursor: 'pointer', 
                          color: '#666', 
                          fontSize: '14px',
                          marginTop: '10px',
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        Verifiera på annan enhet
                      </span>
                      <button 
                        onClick={handleRefresh}
                        className="bank-id-refresh-button"
                      >
                        <FontAwesomeIcon icon={faSync} />
                        Starta om sessionen
                      </button>
                    </div>
                  ) : (
                    <>
                      <button 
                        onClick={launchMobileBankId} 
                        className="bankid-button"
                        style={{ width: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
                      >
                        <img src={BankIdLogoWhite} alt="BankID" style={{ width: '40px', height: '40px' }} />
                        <span className="button-text">Verifiera med BankID</span>
                      </button>
                      <span 
                        onClick={() => setUseQrFallback(true)}
                        style={{ 
                          textDecoration: 'underline', 
                          cursor: 'pointer', 
                          color: '#666', 
                          fontSize: '14px',
                          marginTop: '10px',
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        Verifiera på annan enhet
                      </span>
                    </>
                  )}
                </>
              ) : (
                // QR code fallback for mobile (same as desktop)
                <div className="qr-fallback-container">
                  <div className="fallback-header">
                    <p>Skanna QR-koden med BankID-appen på en annan enhet.</p>
                  </div>
                  
                  {qrCodeData ? (
                    <>
                      <div className="qr-code">
                        <QRCodeSVG 
                          value={qrCodeData}
                          size={200}
                          level="H"
                          includeMargin={true}
                        />
                      </div>
                      
                      <div className="qr-instructions">
                        <p>Skanna QR-koden med BankID-appen på din andra enhet.</p>
                        {timeRemaining !== null && (
                          <div className="qr-timer">
                            <div className="timer-bar">
                              <div 
                                className="timer-progress" 
                                style={{ width: `${(timeRemaining / 30) * 100}%` }}
                              ></div>
                            </div>
                            <p>Uppdateras om: {timeRemaining} sekunder</p>
                          </div>
                        )}
                      </div>
                    
                      {bankIdLoading && (
                        <div className="qr-status">
                          <p>Väntar på BankID<span className="dots-animation"></span></p>
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={generateQrCode}
                      disabled={bankIdLoading}
                      className="bankid-button"
                    >
                      <span className="button-text">{bankIdLoading ? 'Genererar QR-kod...' : 'Visa QR-kod för BankID'}</span>
                    </button>
                  )}
                  
                  <button 
                    onClick={() => {
                      handleRefresh();
                      setUseQrFallback(false);
                    }}
                    className="bank-id-back-button"
                  >
                    Tillbaka till mobil-BankID
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="qr-code-container">
              {qrCodeData ? (
                <>
                  <div className="qr-code">
                    <QRCodeSVG 
                      value={qrCodeData}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  
                  <div className="qr-instructions">
                    <p>Skanna QR-koden med BankID-appen på din mobil.</p>
                    {timeRemaining !== null && (
                      <div className="qr-timer">
                        <div className="timer-bar">
                          <div 
                            className="timer-progress" 
                            style={{ width: `${(timeRemaining / 30) * 100}%` }}
                          ></div>
                        </div>
                        <p>Uppdateras om: {timeRemaining} sekunder</p>
                      </div>
                    )}
                  </div>
                
                  {bankIdLoading && (
                    <div className="qr-status">
                      <p>Väntar på BankID<span className="dots-animation"></span></p>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={generateQrCode}
                  disabled={bankIdLoading}
                  className="bankid-button"
                >
                  <span className="button-text">{bankIdLoading ? 'Genererar QR-kod...' : 'Visa QR-kod för BankID'}</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="verification-footer">
          <button onClick={handleGoHome} className="secondary-button">
            Avbryt och gå hem
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountVerification; 